import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, openAuthPdf, formatApiErrorDetail } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { toast } from "sonner";
import { ShieldCheckIcon as ShieldCheck, DownloadSimpleIcon as DownloadSimple, MagnifyingGlassIcon as MagnifyingGlass, EyeIcon as Eye, EyeSlashIcon as EyeSlash, FloppyDiskIcon as FloppyDisk, ClockCounterClockwiseIcon as ClockCounterClockwise, FunnelIcon as Funnel } from "@phosphor-icons/react";

const THEME_COLORS = { organizational: "#00396a", people: "#0b8457", physical: "#d97706", technological: "#7c3aed" };

const STATI = [
  { v: "non_avviato", l: "Non avviato" },
  { v: "in_corso", l: "In corso" },
  { v: "attuato", l: "Attuato" },
];
const STATO_COLOR = { non_avviato: "bg-rose-50 text-rose-700 border-rose-200", in_corso: "bg-amber-50 text-amber-700 border-amber-200", attuato: "bg-emerald-50 text-emerald-700 border-emerald-200" };

function Kpi({ label, value, accent }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-4" data-testid={`iso-kpi-${label}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 tabular-nums ${accent || ""}`}>{value}</div>
    </Card>
  );
}

export default function Iso27001() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [cid, setCid] = useState("");
  const [soa, setSoa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fStato, setFStato] = useState("all");
  const [revisions, setRevisions] = useState([]);

  const loadClients = () => api.get("/iso27001/clients").then(({ data }) => setClients(data)).catch(() => {});
  const loadRevisions = (id) => api.get(`/iso27001/soa/${id}/revisions`).then(({ data }) => setRevisions(data)).catch(() => setRevisions([]));
  useEffect(() => { loadClients(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? clients.filter((c) => c.ragione_sociale.toLowerCase().includes(s)) : clients;
    return list.slice(0, 200);
  }, [clients, q]);

  const loadSoa = (id) => {
    setLoading(true);
    api.get(`/iso27001/soa/${id}`).then(({ data }) => setSoa(data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  const onPick = (id) => { setCid(id); setFStato("all"); loadSoa(id); loadRevisions(id); };
  useEffect(() => { const qc = sp.get("cliente"); if (qc && qc !== cid) onPick(qc); /* eslint-disable-next-line */ }, [sp]);

  const patchControl = async (code, partial) => {
    setSoa((s) => ({ ...s, controls: s.controls.map((c) => (c.code === code ? { ...c, ...partial } : c)) }));
    try {
      const { data } = await api.patch(`/iso27001/soa/${cid}/controls/${code}`, partial);
      setSoa((s) => ({ ...s, compliance: data.compliance, compliance_by_theme: data.compliance_by_theme || s.compliance_by_theme }));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); loadSoa(cid); }
  };

  const toggleVisibility = async (val) => {
    setSoa((s) => ({ ...s, visibile_area_cliente: val }));
    try {
      await api.patch(`/iso27001/soa/${cid}/visibility`, { visibile: val });
      toast.success(val ? "SoA visibile nell'area cliente" : "SoA nascosta dall'area cliente");
      loadClients();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setSoa((s) => ({ ...s, visibile_area_cliente: !val })); }
  };
  const toggleEdit = async (val) => {
    setSoa((s) => ({ ...s, cliente_puo_modificare: val }));
    try {
      await api.patch(`/iso27001/soa/${cid}/visibility`, { puo_modificare: val });
      toast.success(val ? "Il cliente può modificare i dati" : "Il cliente NON può modificare i dati");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setSoa((s) => ({ ...s, cliente_puo_modificare: !val })); }
  };

  const comp = soa?.compliance;
  const themes = soa?.themes || {};
  const byTheme = soa?.compliance_by_theme || [];
  const shown = soa ? soa.controls.filter((c) => fStato === "all" || (fStato === "escluso" ? !c.applicabile : (c.applicabile && c.stato === fStato))) : [];

  const saveRevision = async () => {
    const label = window.prompt("Etichetta revisione (facoltativa):", "");
    if (label === null) return;
    try {
      await api.post(`/iso27001/soa/${cid}/snapshot`, { label });
      toast.success("Revisione salvata");
      loadRevisions(cid);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="ISO/IEC 27001 — Annex A (SoA)"
          subtitle="Dichiarazione di Applicabilità dei 93 controlli Annex A (2022) per cliente"
          actions={soa && <Button onClick={() => openAuthPdf(`/iso27001/soa/${cid}/pdf`)} className="rounded-sm gap-2" data-testid="iso-export-pdf"><DownloadSimple size={16} /> Scarica PDF</Button>}
        />

        {/* Selezione cliente */}
        <Card className="rounded-sm border-border shadow-none p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cerca cliente</Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ragione sociale…" className="pl-8 rounded-sm bg-white h-9" data-testid="iso-client-search" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Cliente</Label>
              <Select value={cid} onValueChange={onPick}>
                <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="iso-client-select"><SelectValue placeholder="Seleziona un cliente" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {filtered.map((c) => (
                    <SelectItem key={c.cliente_id} value={c.cliente_id}>
                      {c.ragione_sociale}{c.has_soa ? (c.visibile_area_cliente ? "  •  visibile" : "  •  bozza") : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {!soa && !loading && <p className="text-sm text-muted-foreground">Seleziona un cliente per compilare la Dichiarazione di Applicabilità.</p>}
        {loading && <p className="text-sm text-muted-foreground">Caricamento…</p>}

        {soa && !loading && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="text-lg font-heading font-bold">{soa.cliente_nome}</div>
              <div className={`flex items-center gap-3 rounded-sm border px-4 py-2 ${soa.visibile_area_cliente ? "border-emerald-300 bg-emerald-50" : "border-border bg-secondary/40"}`}>
                {soa.visibile_area_cliente ? <Eye size={18} className="text-emerald-600" /> : <EyeSlash size={18} className="text-muted-foreground" />}
                <span className="text-sm">Mostra nell'area cliente</span>
                <Switch checked={!!soa.visibile_area_cliente} onCheckedChange={toggleVisibility} data-testid="iso-visibility-switch" />
              </div>
              <div className="flex items-center gap-3 rounded-sm border px-4 py-2 border-border bg-secondary/40">
                <span className="text-sm">Il cliente può modificare</span>
                <Switch checked={!!soa.cliente_puo_modificare} onCheckedChange={toggleEdit} data-testid="iso-clientedit-switch" />
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              <Kpi label="Conformità" value={`${comp?.pct ?? 0}%`} accent="text-primary" />
              <Kpi label="Applicabili" value={comp?.applicabili ?? 0} />
              <Kpi label="Attuati" value={comp?.attuati ?? 0} accent="text-emerald-700" />
              <Kpi label="In corso" value={comp?.in_corso ?? 0} accent="text-amber-600" />
              <Kpi label="Non avviati" value={comp?.non_avviato ?? 0} accent="text-rose-600" />
            </div>

            {/* Report di conformità per tema + storico revisioni */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <Card className="rounded-sm border-border shadow-none p-4" data-testid="iso-donut-card">
                <div className="text-sm font-semibold mb-2">Conformità per tema</div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={byTheme} dataKey="pct" nameKey="label" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                      {byTheme.map((t) => <Cell key={t.theme} fill={THEME_COLORS[t.theme] || "#64748b"} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v}%`, n]} contentStyle={{ background: "#09090B", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="text-center -mt-[150px] mb-[110px] pointer-events-none">
                  <div className="font-heading text-2xl font-bold text-primary">{comp?.pct ?? 0}%</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Totale</div>
                </div>
              </Card>

              <Card className="rounded-sm border-border shadow-none p-4 lg:col-span-2" data-testid="iso-revisions-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-semibold flex items-center gap-2"><ClockCounterClockwise size={16} /> Storico revisioni</div>
                  <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={saveRevision} data-testid="iso-save-revision"><FloppyDisk size={15} /> Salva revisione</Button>
                </div>
                {revisions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">Nessuna revisione salvata. Salva lo stato attuale per iniziare a tracciare i progressi nel tempo.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Etichetta</TableHead><TableHead className="text-right">Conformità</TableHead><TableHead className="text-right">Attuati</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {revisions.map((r) => (
                        <TableRow key={r.id} data-testid="iso-revision-row">
                          <TableCell className="tabular-nums text-sm">{new Date(r.created_at).toLocaleString("it-IT")}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.label || "—"}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium text-primary">{r.compliance?.pct ?? 0}%</TableCell>
                          <TableCell className="text-right tabular-nums">{r.compliance?.attuati ?? 0}/{r.compliance?.applicabili ?? 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <Funnel size={15} className="text-muted-foreground" />
              <Select value={fStato} onValueChange={setFStato}>
                <SelectTrigger className="w-56 rounded-sm bg-white h-9" data-testid="iso-filter-stato"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i controlli</SelectItem>
                  <SelectItem value="non_avviato">Solo non avviati</SelectItem>
                  <SelectItem value="in_corso">Solo in corso</SelectItem>
                  <SelectItem value="attuato">Solo attuati</SelectItem>
                  <SelectItem value="escluso">Solo esclusi (non applicabili)</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">{shown.length} controlli</span>
            </div>

            <Card className="rounded-sm border-border shadow-none">
              <Table>
                <TableHeader><TableRow>
                  <TableHead className="w-24">Controllo</TableHead><TableHead>Titolo</TableHead>
                  <TableHead className="w-28 text-center">Applicabile</TableHead><TableHead className="w-44">Stato</TableHead><TableHead className="w-[28%]">Giustificazione / Note</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {shown.map((c, i) => {
                    const prev = shown[i - 1];
                    const showTheme = !prev || prev.theme !== c.theme;
                    return (
                      <>
                        {showTheme && (
                          <TableRow key={`t-${c.theme}`} className="bg-secondary/60">
                            <TableCell colSpan={5} className="font-semibold text-primary text-xs uppercase tracking-wider">{themes[c.theme]?.it || c.theme}</TableCell>
                          </TableRow>
                        )}
                        <TableRow key={c.code} data-testid="iso-control-row">
                          <TableCell className="font-medium tabular-nums">A.{c.code}</TableCell>
                          <TableCell className="text-sm">
                            {c.title_it}
                            <button onClick={() => nav(`/tools/iso27001/${cid}/${c.code}`)} className="ml-2 text-xs text-primary underline hover:no-underline" data-testid={`iso-open-${c.code}`}>gestione{c.gestione ? " ✓" : ""}</button>
                          </TableCell>
                          <TableCell className="text-center">
                            <Switch checked={!!c.applicabile} onCheckedChange={(v) => patchControl(c.code, { applicabile: v })} data-testid={`iso-app-${c.code}`} />
                          </TableCell>
                          <TableCell>
                            {c.applicabile ? (
                              <Select value={c.stato} onValueChange={(v) => patchControl(c.code, { stato: v })}>
                                <SelectTrigger className={`h-8 rounded-sm text-xs ${STATO_COLOR[c.stato]}`} data-testid={`iso-stato-${c.code}`}><SelectValue /></SelectTrigger>
                                <SelectContent>{STATI.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : <Badge variant="outline" className="rounded-sm text-muted-foreground">Escluso</Badge>}
                          </TableCell>
                          <TableCell>
                            <Input defaultValue={c.giustificazione} onBlur={(e) => { if (e.target.value !== c.giustificazione) patchControl(c.code, { giustificazione: e.target.value }); }} placeholder="Motivazione / riferimento…" className="h-8 rounded-sm text-xs" data-testid={`iso-note-${c.code}`} />
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>
    </InternalLayout>
  );
}
